import React from "react";

/* ── Mock data ─────────────────────────────────── */

const TEAM = {
  name: "Team name XXXX",
  owner: {
    initials: "D",
    color: "#6B4E6B",
    name: "Emily Johnson",
    email: "student@harvard.edu",
  },
  members: [
    { initials: "T", color: "#4E6B5A", name: "Ethan Carter", email: "contact@mit.edu", isYou: true },
    { initials: "B", color: "#6B524E", name: "Ethan Carter", email: "contact@mit.edu", isYou: false },
    { initials: "B", color: "#956563", name: "Ethan Carter", email: "contact@mit.edu", isYou: false },
    { initials: "X", color: "#4E5E6B", name: "Ethan Carter", email: "contact@mit.edu", isYou: false },
  ],
};

/* ── Avatar ───────────────────────────────────── */

function Avatar({ initials, color }: { initials: string; color: string }) {
  return (
    <div
      className="flex items-center justify-center rounded-full shrink-0 size-[28px]"
      style={{ backgroundColor: color }}
    >
      <span
        className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-white leading-none"
      >
        {initials}
      </span>
    </div>
  );
}

/* ── You badge ────────────────────────────────── */

function YouBadge() {
  return (
    <span
      className="inline-flex items-center px-[6px] py-[2px] font-['Helvetica_Neue:Regular',sans-serif] text-[10px] tracking-[-0.1px]"
      style={{
        backgroundColor: "rgba(59,14,39,0.06)",
        border: "1px solid rgba(59,14,39,0.12)",
        color: "var(--brand-text)",
      }}
    >
      You
    </span>
  );
}

/* ── Member row ───────────────────────────────── */

function MemberRow({
  initials,
  color,
  name,
  email,
  isYou = false,
}: {
  initials: string;
  color: string;
  name: string;
  email: string;
  isYou?: boolean;
}) {
  return (
    <div
      className="flex flex-row items-center gap-[12px] px-[17px] h-[56px]"
      style={{ borderTop: "1px solid rgba(59,14,39,0.1)" }}
    >
      <Avatar initials={initials} color={color} />
      <div className="flex items-center flex-1 min-w-0 justify-between">
        <div className="flex items-center gap-[6px]">
          <span
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[15px] tracking-[-0.3px]"
            style={{ color: "var(--brand-text)" }}
          >
            {name}
          </span>
          {isYou && <YouBadge />}
        </div>
        <span
          className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] tracking-[-0.3px]"
          style={{ color: "var(--brand-text)", opacity: 0.5 }}
        >
          {email}
        </span>
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────── */

export function MyTeamTab() {
  return (
    <div className="w-full">

      {/* Team name heading */}
      <h2
        className="font-['Helvetica_Neue:Condensed_Bold',sans-serif] text-[24px] tracking-[-0.5px] m-0 mb-[24px]"
        style={{ color: "var(--brand-text)" }}
      >
        {TEAM.name}
      </h2>

      {/* Card */}
      <div
        className="w-full"
        style={{ border: "1px solid rgba(59,14,39,0.1)" }}
      >
        {/* Owner section header */}
        <div className="flex items-center px-[17px] h-[44px]">
          <span
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] tracking-[-0.2px]"
            style={{ color: "var(--brand-text)", opacity: 0.45 }}
          >
            Owner
          </span>
        </div>

        {/* Owner row */}
        <MemberRow
          initials={TEAM.owner.initials}
          color={TEAM.owner.color}
          name={TEAM.owner.name}
          email={TEAM.owner.email}
          isYou={false}
        />

        {/* Team Members section header */}
        <div
          className="flex items-center justify-between px-[17px] h-[44px]"
          style={{ borderTop: "1px solid rgba(59,14,39,0.1)" }}
        >
          <span
            className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] tracking-[-0.2px]"
            style={{ color: "var(--brand-text)", opacity: 0.45 }}
          >
            Team Members
          </span>
          <button
            className="bg-transparent border-none cursor-pointer p-0"
          >
            <span
              className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] tracking-[-0.2px]"
              style={{ color: "var(--brand-text)", opacity: 0.5 }}
            >
              离出
            </span>
          </button>
        </div>

        {/* Members 2-column grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-2"
          style={{ borderTop: "1px solid rgba(59,14,39,0.1)" }}
        >
          {TEAM.members.map((m, i) => (
            <div
              key={i}
              style={{
                borderTop: i >= 2 ? "1px solid rgba(59,14,39,0.1)" : undefined,
                borderRight: i % 2 === 0 ? "1px solid rgba(59,14,39,0.1)" : undefined,
              }}
            >
              {/* Re-implement inline to avoid extra borders */}
              <div className="flex flex-row items-center gap-[12px] px-[17px] h-[56px]">
                <Avatar initials={m.initials} color={m.color} />
                <div className="flex items-center flex-1 min-w-0 justify-between">
                  <div className="flex items-center gap-[6px]">
                    <span
                      className="font-['Helvetica_Neue:Regular',sans-serif] text-[15px] tracking-[-0.3px]"
                      style={{ color: "var(--brand-text)" }}
                    >
                      {m.name}
                    </span>
                    {m.isYou && <YouBadge />}
                  </div>
                  <span
                    className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] tracking-[-0.3px]"
                    style={{ color: "var(--brand-text)", opacity: 0.5 }}
                  >
                    {m.email}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
